/** Marker a menu item carries in its URL to become a consent-dialog trigger. */
const MARKER = 'cookie-settings';

const MARKER_HASH = `#${MARKER}`;

/** Narrows the click delegate; `isMarkedLink` is what decides. */
const LINK_SELECTOR = `a[href*="${MARKER_HASH}"]`;

const INTERACTED_CLASS = 'user-interacted';

const isMarkedLink = (link) => {
  try {
    return new URL(link.href, window.location.href).hash === MARKER_HASH;
  } catch (error) {
    console.warn('[consent] could not resolve link href', link.href, error);
    return false;
  }
};

/**
 * Asks whatever overlay is open to close. Escape is the signal the theme's menus, drawers and
 * modals already act on, so this stays a request rather than a list of things to reach into.
 */
const closeOpenOverlays = () =>
  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })
  );

const hasMarker = () => window.location.hash === MARKER_HASH;

/** The dialog is not painted until the document has a gesture on record. */
const recordGesture = () =>
  document.documentElement.classList.add(INTERACTED_CLASS);

const openConsentDialog = () => {
  const api = window.Cookiebot || window.CookieConsent;

  if (typeof api?.renew !== 'function') return false;

  try {
    api.renew();
  } catch (error) {
    console.warn(
      '[consent] renew() threw; leaving the link to navigate.',
      error
    );
    return false;
  }

  return true;
};

const handleClick = (event) => {
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const link = event.target.closest?.(LINK_SELECTOR);

  if (!link || !isMarkedLink(link)) return;

  // The click is about to stop being a navigation, and a navigation is what would otherwise
  // have torn down whatever overlay this link was clicked inside.
  closeOpenOverlays();
  recordGesture();

  // Resolved per click, never cached: the consent manager arrives asynchronously and may never
  // arrive at all. When it is absent the href stands and the visitor gets the linked page.
  if (!openConsentDialog()) {
    console.warn(
      '[consent] consent manager unavailable; following the link instead.'
    );
    return;
  }

  event.preventDefault();
};

/**
 * Turns links whose URL carries the `cookie-settings` marker into consent-dialog triggers: the
 * dialog reopens on the current page instead of the link navigating. Landing on a marked URL
 * opens the dialog too, which is where the un-enhanced click ends up.
 */
export const initConsentLinks = () => {
  // Capture phase: a delegate on `document` still sees the click if something closer to the
  // link stops propagation.
  document.addEventListener('click', handleClick, true);

  let retryArmed = false;
  let declineLogged = false;

  const disarmRetry = () => {
    window.removeEventListener('CookiebotOnLoad', onConsentManagerLoad);
    retryArmed = false;
  };

  function onConsentManagerLoad() {
    // The marker can go while this is armed — the visitor moved on. Disarm either way, or a
    // later arrival could never arm a fresh retry.
    if (!hasMarker()) {
      disarmRetry();
      return;
    }

    if (openOnArrival()) disarmRetry();
  }

  // Armed from the failure branch below, so both arrival routes — page load and a same-page
  // fragment move — recover when the consent manager lands late.
  const armRetry = () => {
    if (retryArmed) return;

    retryArmed = true;
    window.addEventListener('CookiebotOnLoad', onConsentManagerLoad);
  };

  function openOnArrival() {
    // Arriving here is the continuation of a click; record that gesture on this document
    // before asking for the dialog.
    recordGesture();

    if (!openConsentDialog()) {
      if (!declineLogged) {
        console.warn(
          '[consent] arrived on a marked URL with no consent manager; waiting for one.'
        );
        declineLogged = true;
      }

      armRetry();
      return false;
    }

    // Drop the marker so a reload or a back-navigation does not reopen the dialog.
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );

    return true;
  }

  // A marked link clicked from the page it points at only moves the fragment, so the arrival
  // runs on hashchange as well as on load.
  window.addEventListener('hashchange', () => {
    if (hasMarker()) openOnArrival();
  });

  if (hasMarker()) openOnArrival();
};
